import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isFirebaseConfigured } from "../firebase";
import { supportedLanguages, type Lang } from "../i18n";
import { createRoom, joinRoom } from "../game/rooms";
import type { Strictness } from "../game/scoring";

export default function Lobby() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(() => localStorage.getItem("lexikon.displayName") ?? "");
  const [joinCode, setJoinCode] = useState("");
  const [language, setLanguage] = useState<Lang>((i18n.resolvedLanguage as Lang) ?? "en");
  const [interpretSeconds, setInterpretSeconds] = useState<number | null>(60);
  const [strictness, setStrictness] = useState<Strictness>("normal");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fbReady = isFirebaseConfigured();

  function rememberName(name: string) {
    setDisplayName(name);
    localStorage.setItem("lexikon.displayName", name);
  }

  async function handleCreate() {
    if (!displayName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { roomCode } = await createRoom({ displayName, language, interpretSeconds, strictness });
      navigate(`/play/${roomCode}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleJoin() {
    if (!displayName.trim() || !joinCode.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await joinRoom({ roomCode: joinCode.trim().toUpperCase(), displayName });
      navigate(`/play/${joinCode.trim().toUpperCase()}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h2>{t("lobby.title")}</h2>

      {!fbReady && (
        <div className="card" style={{ marginBottom: 16, background: "#fff7e0", borderLeft: "6px solid var(--accent)" }}>
          {t("lobby.firebaseMissing")}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        <section className="card">
          <h3>{t("lobby.create")}</h3>
          <label className="field">
            {t("lobby.displayName")}
            <input value={displayName} onChange={(e) => rememberName(e.target.value)} placeholder="Ada" maxLength={24} />
          </label>
          <label className="field">
            {t("lobby.language")}
            <select value={language} onChange={(e) => setLanguage(e.target.value as Lang)}>
              {supportedLanguages.map((l) => (
                <option key={l} value={l}>{l.toUpperCase()}</option>
              ))}
            </select>
          </label>
          <label className="field">
            Interpretation timer
            <select
              value={interpretSeconds === null ? "off" : String(interpretSeconds)}
              onChange={(e) => setInterpretSeconds(e.target.value === "off" ? null : Number(e.target.value))}
            >
              <option value="off">No timer (composer reveals manually)</option>
              <option value="30">30 seconds</option>
              <option value="60">60 seconds (rulebook)</option>
              <option value="90">90 seconds</option>
              <option value="120">2 minutes</option>
              <option value="300">5 minutes</option>
              <option value="600">10 minutes</option>
            </select>
          </label>
          <label className="field" title="Strict: exact wording only. Normal: common synonyms accepted (kid = child). Easy: synonyms + prefix + typo-tolerant — best for younger players.">
            Scoring strictness
            <select value={strictness} onChange={(e) => setStrictness(e.target.value as Strictness)}>
              <option value="strict">Strict — exact wording only</option>
              <option value="normal">Normal — synonyms accepted (recommended)</option>
              <option value="easy">Easy — synonyms + prefix + typo-tolerant</option>
            </select>
          </label>
          <button className="btn primary" onClick={handleCreate} disabled={!fbReady || !displayName.trim() || busy}>
            {t("lobby.createBtn")}
          </button>
        </section>

        <section className="card">
          <h3>{t("lobby.join")}</h3>
          <label className="field">
            {t("lobby.displayName")}
            <input value={displayName} onChange={(e) => rememberName(e.target.value)} placeholder="Ada" maxLength={24} />
          </label>
          <label className="field">
            {t("lobby.joinCodePlaceholder")}
            <input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="ABCDEF"
              maxLength={6}
              style={{ fontFamily: "monospace", letterSpacing: "0.2em", textTransform: "uppercase" }}
            />
          </label>
          <button className="btn primary" onClick={handleJoin} disabled={!fbReady || !displayName.trim() || joinCode.trim().length !== 6 || busy}>
            {t("lobby.joinBtn")}
          </button>
        </section>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, background: "#fde2e1", borderLeft: "6px solid var(--danger)" }}>
          {error}
        </div>
      )}
    </div>
  );
}
